import React from "react";
import { Tab } from "@headlessui/react";
import { twClassNames } from "../../../../helpers/twClassNames";
import Button from "../../../Button";
import { NumberedStep } from "./NumberedStep";
import { BodyText } from "./BodyText";
import { MarkdownPreview } from "../../../MarkdownPreview";

const TABS: { key: string; label: string; icon: string }[] = [
    { key: "docker", label: "Docker", icon: "fab fa-docker" },
    { key: "macos", label: "MacOS App", icon: "fab fa-apple" },
    { key: "windows", label: "Windows App", icon: "fab fa-windows" }
];

function DownloadAppPanel({ os }: { os: "macos" | "windows" }) {
    const label = os === "macos" ? "MacOS" : "Windows";
    return (
        <div className="flex flex-col gap-2">
            <BodyText>
                This will download the Roboflow Inference Server app for {label} and start a local
                server on your machine.
            </BodyText>
            <div className="flex justify-start">
                <Button
                    type="secondary"
                    onClick={() => {
                        window.open("https://inference.roboflow.com/download", "_blank");
                    }}
                >
                    <i className="far fa-download mr-2" />
                    Download App
                </Button>
            </div>
        </div>
    );
}

function DockerPanel() {
    return (
        <div className="flex flex-col gap-2">
            <BodyText>
                Install our latest inference package and run the following command (see full
                documentation{" "}
                <a
                    href="https://inference.roboflow.com/quickstart/run_a_model/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    here
                </a>
                ):
            </BodyText>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <MarkdownPreview
                    source={`\`\`\`bash
pip install -U inference-cli && inference server start
\`\`\``}
                />
            </div>
            <p className="mt-1 text-xs text-gray-600">
                Requires inference package version {">="}0.9.18.
            </p>
        </div>
    );
}

export function LocalInferenceInstallationStep({
    number = 1,
    collapsible = true
}: {
    number?: number;
    collapsible?: boolean;
}) {
    return (
        <NumberedStep number={number} title="Install the Server" collapsible={collapsible}>
            <Tab.Group>
                <Tab.List className="flex gap-2 border-b border-gray-200">
                    {TABS.map((tab) => (
                        <Tab
                            key={tab.key}
                            className={({ selected }) =>
                                twClassNames(
                                    "whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium focus:outline-none",
                                    selected
                                        ? "border-brand text-brand"
                                        : "border-transparent text-gray-500 hover:text-gray-700"
                                )
                            }
                        >
                            <i className={`${tab.icon} mr-2`} />
                            {tab.label}
                        </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels className="mt-3">
                    <Tab.Panel>
                        <DockerPanel />
                    </Tab.Panel>
                    <Tab.Panel>
                        <DownloadAppPanel os="macos" />
                    </Tab.Panel>
                    <Tab.Panel>
                        <DownloadAppPanel os="windows" />
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </NumberedStep>
    );
}
